/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { TextArea as TextAreaView, useDirection } from '@veneer/core';
import React, { memo, useMemo } from 'react';
import './styles.scss';
import { TextAreaProps } from './types';
import { generateID } from '../../base/helper';

const TTextArea: React.FC<TextAreaProps> = ({ ...props }) => {
  const {
    supportiveText,
    id = generateID('clientos-ui-toolkit-text-area'),
    'aria-label': ariaLabel,
    ...rest
  } = props;
  const { disabled, value, error, label, focused } = props;
  const [focus, setFocus] = React.useState(focused || false);

  const rtl = useDirection().isRTL;

  const getClassName = useMemo(() => {
    let className = 'clientos-ui-toolkit-text-area__container';

    if (disabled) {
      className += ' clientos-ui-toolkit-text-area__container--disabled';
    } else if (value && !error) {
      className += ' clientos-ui-toolkit-text-area__container--with-value';
    } else if (value && error) {
      className +=
        ' clientos-ui-toolkit-text-area__container--with-value-error';
    } else if (error) {
      className += ' clientos-ui-toolkit-text-area__container--error';
    } else {
      className += ' clientos-ui-toolkit-text-area__container--default';
    }

    if (rtl) {
      className += ' rtl';
    }

    return className;
  }, [disabled, error, value, rtl]);

  const getSupportiveTextClassName = useMemo(() => {
    let className = 'text-area-supportive-text';
    if (error) {
      className += ` ${className}--error`;
    } else if (disabled) {
      className += ` ${className}--disabled`;
    }

    return className;
  }, [error, disabled]);

  return (
    <div
      className="clientos-ui-toolkit-text-area"
      id={id}
    >
      <div
        className={getClassName}
        id={`${id}__container`}
      >
        <TextAreaView
          {...rest}
          className={getClassName}
          label={label}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          id={`${id}__textarea`}
        />
      </div>

      {supportiveText && (
        <div
          className={getSupportiveTextClassName}
          id={`${id}__supportive-text`}
        >
          <div
            className="supportive-text"
            id={`${id}__supportive-text__container`}
          >
            <p
              className="caption-regular"
              id={`${id}__supportive-text`}
              aria-label={ariaLabel}
            >
              {supportiveText}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const TextAreaAnalytics = withAnalytics(TTextArea);
export default memo(TTextArea);
